import React from 'react';
import {Box} from "@mui/material";
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";

interface Props {
    label: string;
    hint?: string | null;
    /** Required and still unanswered — shown once the user tried to save. */
    missing?: boolean;
    /** Right-hand control (yes/no toggle, short text input). */
    control: React.ReactNode;
    /** Full-width content under the row (e.g. a safety warning). */
    children?: React.ReactNode;
    testId?: string;
}

/**
 * One row of a divided question list: label (+ hint / "not answered") on the
 * left, the control on the right. Consecutive rows stack without gaps — the
 * first drops its top padding, the last its divider — so a section reads as
 * one list (prototype rows) rather than loose grid items.
 */
const QuestionRow = ({label, hint, missing, control, children, testId}: Props) => {
    const {t} = useTranslation();
    return (
        <Box
            data-testid={testId}
            sx={{
                py: "12px", borderBottom: "1px solid #eeeeee",
                "&:first-of-type": {pt: 0},
                "&:last-of-type": {borderBottom: "none", pb: 0},
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap"}}>
                <Box sx={{flex: 1, minWidth: 220}}>
                    <Typography level="bodyBold" sx={{color: '#333'}}>{label}</Typography>
                    {hint && <Typography level="bodySmall" sx={{color: '#777'}}>{hint}</Typography>}
                    {missing && (
                        <Typography level="body-xs" color="warning" data-testid="doc-missing-hint">{t('Not answered yet')}</Typography>
                    )}
                </Box>
                <Box sx={{flexShrink: 0, minWidth: 0}}>{control}</Box>
            </Box>
            {children}
        </Box>
    );
};

export default QuestionRow;
